<script setup>
import cdnFile from '@/helpers/cdnFile.js'
import { computed, inject, onMounted, reactive, ref, watch } from 'vue'
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { useRoute, useRouter } from 'vue-router'
import { useLoading } from 'vue-loading-overlay'
import { useStore } from 'vuex'
import {
  sidebarOpen,
  touchend,
  touchmove,
  touchstart,
} from '@/helpers/navigationMenu.js'
import { useToast } from 'vue-toast-notification'
import formatMessage from '@/helpers/formatMessage.js'
import Confirm from './Confirm.vue'
import NavigationListItem from './Navigation/NavigationListItem.vue'
import { useMixpanel } from '@/composables/mixpanel'
import AppHeader from './AppHeader.vue'
import { useWindowSize } from '@vueuse/core'
import ButtonComponent from '@/components/ButtonComponent.vue'
import MobileMenu from '@/components/MobileMenu.vue'
import { useNavigationMenu } from '@/composables/useNavigationMenu'
import SvgIcon from '@/components/SvgIcon.vue'
import { generateSrcSet } from '@/helpers/srcSetHelper'
import ModelChatItem from '@/components/ModelChatItem.vue'
import ModelChatItemMobile from '@/components/ModelChatItemMobile.vue'

const mixpanel = useMixpanel()

const $router = useRouter()
const $route = useRoute()
const $store = useStore()
const $axios = inject('axios')
const $toast = useToast()

const chatSessionId = computed(() => $store.state.chatSessionId)

const typing = computed(() => $store.state.typing)
const $loading = useLoading({
  canCancel: false,
  isFullPage: true,
  color: '#a15cff',
})

const pinnedModels = computed(() => user.value?.models.filter((m) => m.pinned))
const unpinnedModels = computed(() =>
  user.value?.models.filter((m) => !m.pinned),
)

const discountPopupOpen = computed(() => $store.state.discountPopupOpen)
const chatView = computed(() => $route.path === '/chat')
const user = computed(() => $store.state.user)
const premadeModel = computed(() => $store.state.premadeModel)
const model = computed(() => $store.getters.model)
const modelActions = reactive({
  delete: null,
  clear: null,
})

const canDeleteModel = computed(() => $store.getters.models.length > 1)
const modelSelectedForChatClear = computed(() =>
  $store.getters.models.find((model) => model.id == modelActions.clear),
)
const modelSelectedForDeletion = computed(() =>
  $store.getters.models.find((model) => model.id == modelActions.delete),
)

function createModel() {
  $router.push('/create-model?from=chat')
}

const isHomePage = computed(() =>
  [
    '/',
    '/ai-boyfriend',
    '/ai-boyfriend/',
    '/ai-hentai',
    '/ai-hentai/',
  ].includes($route.path),
)

const generatorPopupOpen = computed(() => $store.state.generatorPopupOpen)

const currentRoutePath = ref('')
const pathChanged = ref(false)

watch($route, (value) => {
  if (currentRoutePath.value !== '/chat') {
    pathChanged.value = true
  }
})

function selectModel(model) {
  if (typing.value && $store.state.messages.length > 2) return

  sidebarOpen.value = false
  pathChanged.value = false

  console.log('model: ', model)

  $store.commit('SET_SELECTED_MODEL', model.id)
}
function onClearModel(model) {
  modelActions.clear = model.id
}
function onDeleteModel(model) {
  modelActions.delete = model.id
}
async function confirmClearModel() {
  const oldModel = modelActions.clear
  const oldModelId = oldModel
  const loader = $loading.show()

  try {
    const resp = await $axios
      .post('/user/clear-model', {
        modelId: oldModelId,
      })
      .then((response) => response.data)
    $store.commit('ADD_MODEL', resp.model)
    $store.commit('SET_SELECTED_MODEL', resp.model.id)
    $store.commit('DELETE_MODEL', oldModelId)

    modelActions.clear = null
  } catch (e) {
    console.log(e)
    const message = e?.response?.data?.message
    $toast.error(message || 'Unable to perform action. Please try again later.')
  }

  loader.hide()
}
async function confirmDeleteModel() {
  // not allowed to delete models
  if ($store.getters.models.length <= 1) {
    return
  }

  const toBeDeletedModel = modelActions.delete
  const toBeDeletedModelId = toBeDeletedModel

  const loader = $loading.show()
  try {
    await $axios.post('/user/delete-model', {
      modelId: toBeDeletedModelId,
    })
    if (toBeDeletedModelId === $store.state.selectedModel) {
      const nextModel = $store.getters.models.find(
        (model) => model.id !== toBeDeletedModelId,
      )
      $store.commit('SET_SELECTED_MODEL', nextModel.id)
    }
    $store.commit('DELETE_MODEL', toBeDeletedModelId)

    modelActions.delete = null
  } catch (e) {
    console.log(e)
    const message = e?.response?.data?.message
    $toast.error(message || 'Unable to perform action. Please try again later.')
  }

  loader.hide()
}

const windowSize = useWindowSize()

const windowWidth = computed(() => windowSize.width.value)

const {
  goHome,
  goToGenerator,
  goToChat,
  goToSaved,
  goToVideos,
  goToAffiliate,
  goToSettings,
  logout,
} = useNavigationMenu()

const mobileMenuOpen = computed(() => $store.state.mobileMenuOpen)

function toggleMobileMenu(value) {
  $store.commit('SET_MOBILE_MENU_OPEN', {
    open: value,
  })
}

async function toggleConversationPin(model) {
  const pinnedConversations = user?.value?.models.filter(
    (model) => model.pinned,
  ).length
  if (pinnedConversations >= 20 && !model.pinned) {
    $toast.error('You can only pin up to 20 conversations.')
    return
  }

  try {
    model.pinned = !model.pinned
    await $axios.post('/user/pin-conversation', {
      modelId: model.id,
    })
  } catch (e) {
    model.pinned = !model.pinned
    const message =
      e?.response?.data?.message ||
      'Unable to perform action. Please try again later.'
    $toast.error(message)
  }
}
</script>

<template>
  <template v-if="$route.meta.hasSideBar">
    <TransitionRoot
      v-if="chatView && windowWidth < 1024 && !mobileMenuOpen"
      as="template"
      :show="sidebarOpen && chatView"
    >
      <Dialog
        as="div"
        class="relative z-40 lg:hidden"
        @touchstart="touchstart"
        @touchmove="touchmove"
        @touchend="$store.state.selectedModel ? touchend : () => {}"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-[#10091f]/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            :enter-from="pathChanged ? 'translate-x-0' : '-translate-x-full'"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative flex w-full flex-1">
              <div
                class="w-full bg-[#070917] flex flex-col overflow-y-auto overflow-x-hidden pb-[16px] ring-1 ring-white/10"
              >
                <AppHeader />
                <div
                  class="px-4 flex-1"
                  :class="discountPopupOpen ? 'pt-[90px]' : ''"
                >
                  <div
                    class="flex flex-col h-full overflow-y-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                  >
                    <div
                      class="flex justify-center items-center bg-[#0A0D1E] py-[27px] cursor-pointer rounded-tl-[15px] rounded-tr-[15px] border border-white border-opacity-5"
                      @click="createModel()"
                    >
                      <div class="flex flex-col">
                        <div class="text-[19px] mr-[21px]">Create New</div>
                      </div>
                      <div
                        class="flex items-center justify-center h-[30px] w-[30px] rounded-full border border-white border-opacity-15"
                        style="
                          background: radial-gradient(
                            255.14% 174.74% at 38.76% 155.71%,
                            #cc47ff 0%,
                            #9a5cff 100%
                          );
                          box-shadow:
                            0px 0px 2.5px 0px #1cae6e,
                            0px 0px 0px 0.99px #b552ff;
                        "
                      >
                        <SvgIcon icon-id="hero-plus" class="h-6 w-6" />
                      </div>
                    </div>
                    <div
                      class="flex flex-col gap-[15px] flex-1 bg-[#0A0D1E] py-[17px] rounded-b-[15px] border border-t-0 border-white border-opacity-5"
                    >
                      <ModelChatItemMobile
                        v-for="displayModel in pinnedModels"
                        :key="displayModel.id"
                        :displayModel="displayModel"
                        :selectedModelId="model?.id"
                        :user="user"
                        :canDeleteModel="canDeleteModel"
                        @select="selectModel"
                        @clear="onClearModel"
                        @delete="onDeleteModel"
                        @togglePin="toggleConversationPin"
                      />
                      <div
                        class="border-b border-[#343755] border-dashed mx-3 mb-1"
                      ></div>
                      <ModelChatItemMobile
                        v-for="displayModel in unpinnedModels"
                        :key="displayModel.id"
                        :displayModel="displayModel"
                        :selectedModelId="model?.id"
                        :user="user"
                        :canDeleteModel="canDeleteModel"
                        @select="selectModel"
                        @clear="onClearModel"
                        @delete="onDeleteModel"
                        @togglePin="toggleConversationPin"
                      />
                    </div>
                    <div
                      v-if="premadeModel"
                      class="flex gap-x-3 items-center px-6 py-3 cursor-pointer min-h-14 relative"
                      :class="
                        model?.id === premadeModel.id ? 'bg-gray-600' : ''
                      "
                      @click="selectModel(premadeModel)"
                    >
                      <div
                        class="h-14 w-14 bg-gray-500 rounded-full overflow-hidden shrink-0"
                      >
                        <img
                          class="w-full rounded-xl aspect-[832/1216] no-image-select"
                          :src="
                            generateSrcSet(
                              'models',
                              premadeModel.name,
                              'webp',
                              'xs',
                              true,
                            )
                          "
                          :alt="premadeModel.name"
                          draggable="false"
                        />
                      </div>
                      <div class="flex flex-col w-full truncate">
                        <div class="font-semibold">
                          {{ premadeModel.name }}
                        </div>
                        <div
                          class="text-sm truncate max-w-full"
                          v-html="
                            premadeModel?.messages?.[0]?.imageDescription
                              ? 'Sent a picture'
                              : formatMessage(
                                  premadeModel?.messages?.[0]?.content,
                                )
                          "
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <MobileMenu v-if="!generatorPopupOpen" />

    <div
      class="hidden relative py-[18px] pl-[15px] z-50 lg:fixed lg:inset-y-0 lg:z-5 lg:flex rounded-xl"
      :class="chatView ? 'lg:w-[463px]' : 'lg:w-70'"
      :style="{
        top: discountPopupOpen ? '3.5rem' : '0px',
        height: discountPopupOpen ? 'calc(100vh - 3.5rem)' : '100vh',
      }"
    >
      <div
        class="bg-[#070917] flex justify-center grow flex-row gap-y-5 rounded-[25px]"
        :class="chatView ? '' : 'expand'"
      >
        <nav
          class="flex flex-col justify-center pt-[50px] rounded-[25px] border-[1px] w-[250px] border-[#FFFFFF0D]"
          style="
            background: linear-gradient(0deg, #0a0d1e, #0a0d1e),
              radial-gradient(
                123.96% 123.96% at 18.89% 102.14%,
                rgba(76, 12, 81, 0.2) 0%,
                rgba(37, 6, 38, 0) 100%
              );
          "
          :class="chatView ? 'max-w-[101px] rounded-r-none pt-[20px]' : ''"
        >
          <div
            class="flex flex-1 flex-col gap-y-[15px]"
            :class="chatView ? 'mx-auto' : 'mx-8'"
          >
            <router-link
              v-if="!chatView"
              to="/"
              class="flex h-10 shrink-0 justify-center items-center mb-[45px]"
              @click.prevent.stop.capture="goHome(true)"
              ><SvgIcon
                sprite="home"
                icon-id="logo"
                class="w-[153px] aspect-[865/120]"
                alt="Logo"
            /></router-link>
            <router-link
              v-else
              to="/"
              @click.prevent.stop.capture="goHome(true)"
              class="mb-[45px] flex justify-center items-center group h-10 gap-x-3 rounded-[15px] p-2 text-sm font-semibold leading-6 text-gray-100"
            >
              <SvgIcon
                icon-id="logoicon"
                class="h-[22px] w-[22px]"
                alt="Logo"
              />
            </router-link>
            <NavigationListItem
              :to="isHomePage ? $route.path : '/'"
              label="Explore"
              icon-sprite="home"
              icon-id="compass"
              gray-icon-sprite="main"
              gray-icon-id="compass-grey"
              :active="isHomePage"
              :hideLabel="chatView"
              @click.prevent.stop.capture="goHome(false)"
            />
            <NavigationListItem
              to="/nsfw-ai-image-generator"
              label="Generator"
              icon-sprite="generator"
              icon-id="generator"
              gray-icon-sprite="home"
              gray-icon-id="generator-grey"
              :active="$route.path === '/nsfw-ai-image-generator'"
              :hideLabel="!!chatView"
              @click.prevent.stop.capture="goToGenerator"
            />
            <NavigationListItem
              to="/chat"
              label="Chat"
              icon-sprite="main"
              icon-id="chat"
              gray-icon-sprite="home"
              gray-icon-id="chat-grey"
              :active="$route.path === '/chat'"
              :hideLabel="chatView"
              @click.prevent.stop.capture="goToChat"
            />
            <NavigationListItem
              to="/saved"
              label="Saved"
              icon-sprite="main"
              icon-id="saved"
              gray-icon-sprite="home"
              gray-icon-id="saved-grey"
              :active="$route.path === '/saved'"
              :hideLabel="!!chatView"
              @click.prevent.stop.capture="goToSaved"
            />
            <NavigationListItem
              to="/videos"
              label="Videos"
              icon-sprite="main"
              icon-id="video"
              gray-icon-sprite="home"
              gray-icon-id="video-grey"
              :active="$route.path === '/videos'"
              :hideLabel="!!chatView"
              @click.prevent.stop.capture="goToVideos"
            />
          </div>
          <div
            class="flex flex-col gap-[5px] m-[12px] mt-auto py-3 px-[11px] rounded-[20px]"
          >
            <NavigationListItem
              v-if="user"
              to="/"
              label="Logout"
              icon-sprite="main"
              icon-id="logout"
              gray-icon-sprite="main"
              gray-icon-id="logout-grey"
              :active="false"
              :hideLabel="chatView"
              @click.prevent.stop.capture="logout"
            />
          </div>
          <div
            class="flex flex-col gap-[5px] m-[10px] mt-auto py-3 px-[11px] border-[1px] border-[#1B1F36] rounded-[20px]"
            :class="chatView ? 'mb-[15px] mx-[15.5px]' : ''"
          >
            <NavigationListItem
              to="/affiliate"
              label="Affiliate"
              icon-sprite="affiliate"
              icon-id="referral"
              gray-icon-sprite="home"
              gray-icon-id="referral-grey"
              :active="$route.path === '/affiliate'"
              :hideLabel="chatView"
              class="w-full"
              @click="goToAffiliate"
            />
            <NavigationListItem
              to="/settings"
              label="Settings"
              icon-sprite="main"
              icon-id="settings"
              gray-icon-sprite="home"
              gray-icon-id="settings-grey"
              :active="$route.path === '/settings'"
              :hideLabel="chatView"
              class="w-full"
              @click.prevent.stop.capture="goToSettings"
            />
          </div>
        </nav>
        <div
          v-if="chatView"
          class="w-[362px] bg-[#0A0D1E] overflow-y-scroll overflow-x-hidden border border-l-0 border-white border-opacity-5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          <div class="flex flex-col">
            <div
              class="flex items-center justify-center gap-x-[21px] px-2 py-[25px] cursor-pointer mb-[17px] border-b border-white border-opacity-5"
              @click="createModel()"
            >
              <p class="text-[19px]">Create New</p>
              <div
                class="h-[30px] w-[30px] rounded-full flex items-center justify-center border border-white border-opacity-15"
                style="
                  background: radial-gradient(
                    255.14% 174.74% at 38.76% 155.71%,
                    #cc47ff 0%,
                    #9a5cff 100%
                  );
                  box-shadow:
                    0px 0px 2.5px 0px #1cae6e,
                    0px 0px 0px 0.99px #b552ff;
                "
              >
                <SvgIcon icon-id="hero-plus" class="h-6 w-6" />
              </div>
            </div>
            <ModelChatItem
              v-for="displayModel in pinnedModels"
              :key="displayModel.id"
              :displayModel="displayModel"
              :selectedModelId="model?.id"
              :user="user"
              :canDeleteModel="canDeleteModel"
              @select="selectModel"
              @clear="onClearModel"
              @delete="onDeleteModel"
              @togglePin="toggleConversationPin"
            />
            <div
              v-if="user?.models.filter((model) => model.pinned).length > 0"
              class="border-b border-[#343755] border-dashed mx-3 mb-4"
            ></div>
            <ModelChatItem
              v-for="displayModel in unpinnedModels"
              :key="displayModel.id"
              :displayModel="displayModel"
              :selectedModelId="model?.id"
              :user="user"
              :canDeleteModel="canDeleteModel"
              @select="selectModel"
              @clear="onClearModel"
              @delete="onDeleteModel"
              @togglePin="toggleConversationPin"
            />
            <div
              v-if="premadeModel"
              class="flex gap-x-[15px] items-center bg-[#0E122A] mx-[12px] mb-[15px] py-[22px] px-[15px] rounded-[15px] border-[1px] cursor-pointer min-h-14 relative"
              :class="
                model?.id === premadeModel.id
                  ? 'border-[#B651FF] selected-chat'
                  : 'border-transparent'
              "
              @click="selectModel(premadeModel)"
            >
              <div
                class="h-[43px] w-[43px] bg-gray-500 rounded-full overflow-hidden shrink-0"
              >
                <img
                  class="w-full rounded-xl aspect-[832/1216] no-image-select"
                  :src="
                    generateSrcSet(
                      'models',
                      premadeModel.name,
                      'webp',
                      'xs',
                      true,
                    )
                  "
                  :alt="premadeModel.name"
                  draggable="false"
                />
              </div>
              <div class="flex flex-col w-full truncate">
                <div class="flex items-center gap-[9px]">
                  <div class="">{{ premadeModel.name }}</div>
                  <button
                    v-if="user?.subscription"
                    @click.stop="toggleConversationPin(premadeModel)"
                    class="flex justify-center items-center bg-[#11152D] p-1.5 my-1.5 rounded-full"
                    style="
                      box-shadow:
                        0px 0px 0px 1.2px #1e1a3d,
                        0px 0px 12px 2.4px #13142e inset;
                    "
                  >
                    <SvgIcon
                      v-if="!premadeModel.pinned"
                      icon-id="push-pin-outline"
                      class="w-4 h-4"
                    />
                    <SvgIcon v-else icon-id="push-pin" class="w-4 h-4" />
                  </button>
                </div>
                <div
                  class="text-[13px] text-[#B1B5DB] truncate max-w-full"
                  v-html="
                    premadeModel?.messages?.[0]?.imageDescription
                      ? 'Sent a picture'
                      : formatMessage(premadeModel?.messages?.[0]?.content)
                  "
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Confirm
        popupStyle="bg-[#0A0D22] w-[625px] pt-[50px] pb-5 px-4 lg:py-[75px] mx-4 lg:mx-0 lg:px-[122px]"
        :open="modelActions.delete !== null"
        type="confirm"
        @update:open="
          (v) => {
            if (!v) modelActions.delete = null
          }
        "
        @confirm="confirmDeleteModel"
      >
        <div>
          <h2 class="text-center">
            Are you sure you want to delete
            {{ modelSelectedForDeletion?.name }}? This will also delete all
            pictures in this chat if you haven't saved them.
          </h2>
        </div>
      </Confirm>
      <Confirm
        popupStyle="bg-[#0A0D22] w-[625px] pt-[50px] pb-5 px-4 lg:py-[75px] mx-4 lg:mx-0 lg:px-[122px]"
        :open="modelActions.clear !== null"
        type="confirm"
        @update:open="
          (v) => {
            if (!v) modelActions.clear = null
          }
        "
        @confirm="confirmClearModel"
      >
        <div>
          <h2 class="text-center">
            Are you sure you want to clear your chat with
            {{ modelSelectedForChatClear?.name }}? This will also delete all
            pictures in this chat if you haven't saved them.
          </h2>
        </div>
      </Confirm>
    </div>
  </template>
</template>

<style scoped>
.selected-chat {
  box-shadow: 0px 0px 0px 3px #b552ff26;
}
</style>
